'use client'

import { useRouter } from 'next/navigation'
import { useCallback, useRef } from 'react'
import type { NotificationListItem, NotificationTab } from '@/lib/api/notification'
import { inboxAnalytics, toInboxNotificationType, toInboxTabType } from '@/lib/utils/analytics'
import { resolveDeepLinkUrl } from '@/lib/utils/deepLink'
import { navigatePush } from '@/lib/utils/navigation'
import { useModalStore } from '@/store/modalStore'

interface UseNotificationNavigationOptions {
  tab: NotificationTab
  mode?: 'page' | 'modal'
  onOpenDetail?: (item: NotificationListItem) => void
  beforeNavigate?: () => void
}

export interface UseNotificationNavigationResult {
  openItem: (item: NotificationListItem) => void
  openPath: (item: NotificationListItem, path: string) => void
}

export function useNotificationNavigation({
  tab,
  mode = 'page',
  onOpenDetail,
  beforeNavigate,
}: UseNotificationNavigationOptions): UseNotificationNavigationResult {
  const router = useRouter()
  const closeModal = useModalStore((state) => state.closeModal)
  const navigatingRef = useRef(false)

  const trackClick = useCallback(
    (item: NotificationListItem) => {
      inboxAnalytics.messageClicked(toInboxTabType(tab), toInboxNotificationType(item.bizType))
    },
    [tab],
  )

  const openPath = useCallback(
    (item: NotificationListItem, path: string) => {
      if (!path || navigatingRef.current) return
      navigatingRef.current = true
      trackClick(item)
      beforeNavigate?.()
      closeModal('notification')
      navigatePush(router, path)
      navigatingRef.current = false
    },
    [beforeNavigate, closeModal, router, trackClick],
  )

  const openItem = useCallback(
    (item: NotificationListItem) => {
      if (item.tab === 3 || navigatingRef.current) return
      navigatingRef.current = true
      trackClick(item)

      if (mode === 'modal') {
        onOpenDetail?.(item)
        navigatingRef.current = false
        return
      }

      const route = item.jumpUrl ? resolveDeepLinkUrl(item.jumpUrl) : null
      beforeNavigate?.()
      closeModal('notification')
      navigatePush(router, route?.primaryPath || `/notification/${item.id}`)
      navigatingRef.current = false
    },
    [beforeNavigate, closeModal, mode, onOpenDetail, router, trackClick],
  )

  const result: UseNotificationNavigationResult = { openItem, openPath }
  return result
}
